import {
  Component,
  Output,
  Input,
  EventEmitter,
  ViewChild,
  NgZone,
} from '@angular/core';
import * as debug from 'debug';
import { Content, DomController } from 'ionic-angular';
import * as _ from 'lodash';

function assert(d, s) {
  if (!d) throw new Error(s);
}

@Component({
  selector: 'custom-infinite-scroll',
  templateUrl: 'custom-infinite-scroll.html',
})
export class CustomInfiniteScrollComponent {
  log = debug('debug:app:CustomInfiniteScrollComponent');

  @Output() infinite = new EventEmitter();
  @ViewChild('container') container;

  _lastCheck: number = 0;
  _highestY: number = 0;
  _thr: string = '15%';
  _thrPx: number = 0;
  _thrPc: number = 0.15;
  _position: string = POSITION_BOTTOM;
  _init: boolean = false;

  subs = [];

  _parent;
  _parentIsContent;

  /**
   * @internal
   */
  state: string = STATE_ENABLED;

  /**
   * @input {string} The threshold distance from the bottom
   * of the content to call the `infinite` output event when scrolled.
   * The threshold value can be either a percent, or
   * in pixels. For example, use the value of `10%` for the `infinite`
   * output event to get called when the user has scrolled 10%
   * from the bottom of the page. Use the value `100px` when the
   * scroll is within 100 pixels from the bottom of the page.
   * Default is `15%`.
   */
  @Input()
  get threshold(): string {
    return this._thr;
  }
  set threshold(val: string) {
    this._thr = val;
    if (val.indexOf('%') > -1) {
      this._thrPx = 0;
      this._thrPc = parseFloat(val) / 100;
    } else {
      this._thrPx = parseFloat(val);
      this._thrPc = 0;
    }
  }

  constructor(
    private content: Content,
    private _zone: NgZone,
    private _dom: DomController
  ) {}

  ngOnInit() {
    const parent = (this._parent = this.getScrollParent(
      this.container.nativeElement
    ));
    // this.log('scroll parent', parent);

    if (!parent) {
      return;
    }

    let fn = e => {
      setTimeout(() => {
        this.onScroll(e);
      });
    };

    if (parent.classList.contains('scroll-content')) {
      // use this.content
      // this.log('use scroll parent');
      this.subs.push(this.content.ionScroll.subscribe(fn));
      this._parentIsContent = true;
    } else {
      // custom watch
      // this.log('custom watch');
      parent.addEventListener('scroll', fn);
      this.subs.push(() => {
        parent.removeEventListener('scroll', fn);
      });
      this._parentIsContent = false;
    }
  }

  private getScrollParent(node) {
    if (node == null) {
      return null;
    }

    const o = window.getComputedStyle(node).overflowY;
    if (o === 'auto' || o === 'scroll') {
      return node;
    } else {
      return this.getScrollParent(node.parentNode);
    }
  }

  private getContentDimensions() {
    if (this._parentIsContent) {
      return this.content.getContentDimensions();
    } else {
      return {
        contentHeight: this._parent.offsetHeight,
        scrollHeight: this._parent.scrollHeight,
        scrollTop: this._parent.scrollTop,
      };
    }
  }

  private onScroll(ev) {
    if (this.state === STATE_LOADING || this.state === STATE_DISABLED) {
      return 1;
    }

    if (this._lastCheck + 32 > ev.timeStamp) {
      // no need to check less than every XXms
      return 2;
    }
    this._lastCheck = ev.timeStamp;

    // ******** DOM READ ****************
    const infiniteHeight = this.container.nativeElement.scrollHeight;
    if (!infiniteHeight) {
      // if there is no height of this element then do nothing
      return 3;
    }

    // ******** DOM READ ****************
    const d = this.getContentDimensions();
    // this.log('dimensions', d);
    const height = d.contentHeight;

    const threshold = this._thrPc ? height * this._thrPc : this._thrPx;

    // ******** DOM READS ABOVE / DOM WRITES BELOW ****************

    let distanceFromInfinite: number;

    if (this._position === POSITION_BOTTOM) {
      distanceFromInfinite =
        d.scrollHeight - infiniteHeight - d.scrollTop - height - threshold;
    } else {
      assert(this._position === POSITION_TOP, '_position should be top');
      distanceFromInfinite = d.scrollTop - infiniteHeight - threshold;
    }

    if (distanceFromInfinite < 0) {
      // ******** DOM WRITE ****************
      this._dom.write(() => {
        this._zone.run(() => {
          if (this.state !== STATE_LOADING && this.state !== STATE_DISABLED) {
            this.state = STATE_LOADING;
            // this.log('EMIT');
            this.infinite.emit(this);
          }
        });
      });
      return 5;
    }

    return 6;
  }

  /**
   * Call `complete()` within the `infinite` output event handler when
   * your async operation has completed. For example, the `loading`
   * state is while the app is performing an asynchronous operation,
   * such as receiving more data from an AJAX request to add more items
   * to a data list. Once the data has been received and UI updated, you
   * then call this method to signify that the loading has completed.
   * This method will change the infinite scroll's state from `loading`
   * to `enabled`.
   */
  private complete() {
    if (this.state !== STATE_LOADING) {
      return;
    }

    if (this._position === POSITION_BOTTOM) {
      this.state = STATE_ENABLED;
      return;
    }

    assert(this._position === POSITION_TOP, 'position should be top');
    /* New content is being added at the top, but the scrollTop position stays the same,
      which causes a scroll jump visually. This algorithm makes sure to prevent this.
      (Frame 1)
        complete() is called, but the UI hasn't had time to update yet.
        Save the current content dimensions.
        Wait for the next frame using _dom.read, so the UI will be updated.
      (Frame 2)
        Read the new content dimensions.
        Calculate the height difference and the new scroll position.
        Delay the scroll position change until other possible dom reads are done using _dom.write to be performant.
      (Still frame 2, if I'm correct)
        Change the scroll position (= visually maintain the scroll position).
        Change the state to re-enable the InfiniteScroll. This should be after changing the scroll position, or it could cause the InfiniteScroll to be triggered again immediately.
      (Frame 3)
        Done.
    */

    if (this._parentIsContent) {
      // ******** DOM READ ****************
      // Save the current content dimensions before the UI updates
      const prevDim = this.content.getContentDimensions();

      // ******** DOM READ ****************
      this._dom.read(() => {
        // UI has updated, save the new content dimensions
        const newDim = this.content.getContentDimensions();

        // New content was added on top, so the scroll position should be changed immediately to prevent it from jumping around
        const newScrollTop =
          newDim.scrollHeight - (prevDim.scrollHeight - prevDim.scrollTop);

        // ******** DOM WRITE ****************
        this._dom.write(() => {
          this.content.scrollTop = newScrollTop;
          this.state = STATE_ENABLED;
        });
      });
    }
  }

  private unsubscribeEvents() {
    _.each(this.subs, s => {
      if (s instanceof Function) {
        s();
      } else if (s.unsubscribe) {
        s.unsubscribe();
      }
    });
  }

  ngOnDestroy() {
    this.unsubscribeEvents();
  }
}

const STATE_ENABLED = 'enabled';
const STATE_DISABLED = 'disabled';
const STATE_LOADING = 'loading';

const POSITION_TOP = 'top';
const POSITION_BOTTOM = 'bottom';



// WEBPACK FOOTER //
// ./src/components/custom-infinite-scroll/custom-infinite-scroll.ts